import { CARD_H, CARD_W } from "@webscan/constants.js";
import { warpCard } from "@webscan/warp.js";
import type { Loaded } from "./assets";
import { ort } from "./assets";
import { CAMERA_FPS, closeCamera, type Camera, type CameraSurface } from "./camera";
import { CameraContentProbe } from "./content-probe";
import { DetectorCadence } from "./detector-cadence";
import {
  FOCUS_DECAY,
  FOCUS_RATIO,
  FOCUS_RETRY_MS,
  FOCUS_WAIT_MS,
  HandheldGate,
  HOLD_LABEL,
  MAX_RETAKES,
  RETAKE_DELAYS,
} from "./gate";
import { identify, refineCollector } from "./identify";
import { printingStillOpen } from "./collector-evidence";
import {
  CollectorSampler,
  COLLECTOR_MIN_HEIGHT,
  COLLECTOR_MAX_WIDTH,
  sensorCardHeight,
  sensorQuad,
  type CollectorEvidence,
} from "./collector-sampling";
import { sharperSample, COLLECTOR_MAX_RETRIES, COLLECTOR_RETRY_MS } from "./read-selection";
import type { LetterboxedFrame, PoseDetection } from "./pose";
import {
  focusScore,
  letterboxer,
  quadThumb,
  rasteriser,
  SWAP_DIFFERENCE,
  thumbDifference,
  warpRasterFor,
} from "./sampling";
import { GlSampler } from "./warp-gl";
import { VideoFrames } from "./video-frames";
import type { Emitter } from "../protocol/bus";
import type { Framing, Frame, Identification, ScannerEvents, StatusKind } from "../protocol/events";
import { captureKey, encodeCapture, HQ_SCALE, saveCapture, shortlist } from "../shared/captures";
import { clock, log, ms, trace, warn } from "../shared/debug";
import { applyHomography, CROP_MARGIN, keystonePx } from "../shared/geometry";
import { isIos, isMobile } from "../shared/platform";
import { settings } from "../shared/settings";
import { retainFrame, releaseFrame, type PixelSource } from "./camera-frame";

interface ReadSample {
  card: Uint8ClampedArray;
  hq: Uint8ClampedArray | null;
  collector: CollectorEvidence;
  quadFlat: Float32Array;
  windowAspect: number;
  placementId: number;
  epoch: number;
  at: number;
  prepareMs: number;
  focus: number;
  score: number;
  aspect: number;
  thumb: Float32Array | null;
}

/**
 * The scan loop: camera frames in, verdicts out.
 *
 * This is the whole of what runs while the camera is open, and none of it can see the
 * screen. What it needs from the view — where to look, and whether the user is holding
 * the screen — arrives through {@link SessionInputs}; what it has to say — every
 * detection, every status line, every card it names — leaves as events on the emitter.
 * The view presents a native camera preview and result feedback; neither can change
 * a verdict. Diagnostic consumers can subscribe to the detected frame geometry.
 */

/** Chrome-only, and absent on Safari — but when present it settles a leak question. */
function perfMemory(): string | null {
  const memory = (performance as { memory?: { usedJSHeapSize: number } }).memory;
  return memory ? (memory.usedJSHeapSize / 1e6).toFixed(0) : null;
}

/** A card already logged is not logged again until it has been away this long. */
const REPEAT_MS = 6000;

/**
 * Mobile-only energy and interaction policy — and there is deliberately no pause after a
 * card is named any more.
 *
 * There was: a second at first, then 250 ms, on the theory that the card just read is
 * still lying there and detection spent on it is wasted GPU. Two things were wrong with
 * that. It bought its energy in the one place the scanner is being watched, so the
 * tracking marker outlived its own evidence and sat at full opacity over a card that may
 * already have been lifted away. And it was blind time immediately after the chime —
 * which is precisely when the next card arrives, since the thing that reliably precedes a
 * swap is the previous card having just been named. A gate that cannot see the exchange
 * hands the new card the old one's latch, and the phone this runs on turns out to have
 * had the headroom all along.
 *
 * What remains is a mild guard against a pathological stretch of junk placements — a
 * patterned table, a sleeve pile — where every settle reads and every read rejects.
 */
const REJECT_BACKOFF_AFTER = 8;
const REJECT_BACKOFF_MS = 120;
/**
 * How long detection stays off after the view reports the user has let go of the
 * screen. UI work and detection contend for the same main thread and, on GPU backends,
 * the same device process; the short tail covers click and input handlers and the
 * inertial scroll that follows a flick.
 */
export const INTERACTION_SETTLE_MS = 80;

/** What the loop reads from the view side, as the latest word rather than a stream. */
export interface SessionInputs {
  /** What to look through, or null until the view has said. */
  framing(): Framing | null;
  /** Whether the user is holding the screen — a finger down, a panel open, the settle tail. */
  interacting(): boolean;
}

export interface Session {
  /** Start the loop. Called once, after the view has been told the session started. */
  run(): void;
  /** End the loop and release the camera. Idempotent. */
  stop(): void;
}

/**
 * Build the loop over an open camera. Nothing runs until {@link Session.run}.
 *
 * Separated from opening the camera so the caller can announce the session — and
 * collect the view's first framing — before the first frame is pulled.
 */
export function createSession(
  loaded: Loaded,
  surface: CameraSurface,
  camera: Camera,
  out: Emitter<ScannerEvents>,
  inputs: SessionInputs,
): Session {
  const ios = isIos();
  const mobile = isMobile();
  // A 2x raw keepsake is 5.3 MB plus a same-sized native canvas backing store. Desktop
  // keeps the extra detail; iOS records the canonical 488x680 warp so each completed
  // scan does not create that pressure spike next to the model-runtime allocations.
  const captureScale = ios ? 1 : HQ_SCALE;

  let running = true;
  const frameBoundary = new VideoFrames(surface, Math.ceil(1000 / CAMERA_FPS));
  const cadence = new DetectorCadence();
  const contentProbe = new CameraContentProbe();
  const collectorSampler = new CollectorSampler();
  let sharpened: ReadSample | null = null;
  let evidenceEpoch = 0;
  let latestPose: { placementId: number; frame: Frame } | null = null;
  let refinement: {
    readId: string;
    placementId: number;
    epoch: number;
    identification: Identification;
    attempts: number;
    nextAt: number;
    closer: boolean;
  } | null = null;
  let probeFraming: Framing | null = null;
  const resetFrameEvidence = (): void => {
    evidenceEpoch++;
    sharpened = null;
    refinement = null;
    latestPose = null;
    contentProbe.reset();
    probeTotal = 0;
    cadence.resetEvidence(performance.now());
    probeFraming = null;
  };
  const lifetime = new AbortController();
  const seen = new Map<string, number>();
  const gate = new HandheldGate();
  /**
   * What the card that latched the current placement looks like — the reference a gap in
   * the evidence is proven against. Null until the first read of the session.
   */
  let latchThumb: Float32Array | null = null;
  /**
   * The sharpest crop this session has produced, decayed a little on every measurement.
   *
   * The bar a crop has to clear before a read is spent on it is a fraction of this rather
   * than a number chosen here — see {@link FOCUS_RATIO}.
   */
  let focusBar = 0;
  /** The one read currently running — drives status and prevents snapshot backlogs. */
  let readsInFlight = 0;
  /**
   * Recognition is detached from the frame loop, but strictly single-flight.
   *
   * `identify` spends most of its 200-750 ms awaiting the GPU embed, the ORB worker
   * and the descriptor range fetches; detaching it lets camera frames keep flowing
   * through detection during those waits instead of freezing the viewfinder. The gate
   * is not allowed to fire while one runs, so a second raw card snapshot can never be
   * queued behind it; a new placement remains tracked and fires when the slot is free.
   */
  // Do not add another long-lived GPU context on iOS. In WebGL mode ONNX owns the WebGL2
  // context; in WebGPU mode this avoids running a separate WebGL sampler beside it. Frame
  // sampling uses bounded 2D canvases while the native <video> remains the preview.
  // `?samplergl` exists only to diagnose that isolation and is deliberately separate from
  // `?webgl`, which selects the inference backend.
  const allowWebGl = !ios || /[?&]samplergl\b/.test(location.search);
  const shared = allowWebGl
    ? GlSampler.create(
        Math.max(COLLECTOR_MAX_WIDTH, CARD_W * captureScale, loaded.detector.width),
        Math.max(CARD_H * captureScale, loaded.detector.height),
      )
    : null;
  const letterboxCanvas = letterboxer();
  const grabWarp = rasteriser();
  log(
    "detect",
    `readback path: ${shared ? "one shared WebGL context" : "2D canvas"}` +
      (ios && !allowWebGl ? " (iOS: no second GL context)" : ""),
  );

  // Some handsets renegotiate the track on rotation and the raster changes under the
  // loop. The view frames against the raster, so it has to hear about it.
  const onSurfaceResize = (): void => {
    if (!running || !surface.videoWidth || !surface.videoHeight) return;
    if (surface.videoWidth === camera.sensorWidth && surface.videoHeight === camera.sensorHeight) {
      return;
    }
    camera.sensorWidth = surface.videoWidth;
    camera.sensorHeight = surface.videoHeight;
    resetFrameEvidence();
    log("detect", `sensor raster changed to ${camera.sensorWidth}x${camera.sensorHeight}`);
    out.emit("sensor:resized", {
      sensorWidth: camera.sensorWidth,
      sensorHeight: camera.sensorHeight,
    });
  };
  surface.addEventListener("resize", onSurfaceResize);

  let previousStatus: { text: string; kind: StatusKind } | undefined;
  const status = (text: string, kind: StatusKind): void => {
    if (previousStatus?.text === text && previousStatus.kind === kind) return;
    previousStatus = { text, kind };
    out.emit("status", previousStatus);
  };

  // A paused track keeps its last frame on the surface: iOS interrupts capture for a
  // call or for Siri, Android for a page it has backgrounded. Detecting that frame would
  // spend reads on a picture that stopped arriving, so the loop sits the pause out as
  // blind time the gate is told about — the same footing as a finger on the screen.
  const track = camera.stream.getVideoTracks()[0] ?? null;
  let muted = track?.muted ?? false;
  let captureEpoch = 0;
  const onMute = (): void => {
    muted = true;
    captureEpoch++;
    frameBoundary.cancel();
    resetFrameEvidence();
    gate.blind();
    log("detect", "camera track muted — the device has paused capture");
    status("camera paused by your device…", "paused");
  };
  const onUnmute = (): void => {
    muted = false;
    log("detect", "camera track unmuted — capture resumed");
    status("looking…", "looking");
  };
  track?.addEventListener("mute", onMute);
  track?.addEventListener("unmute", onUnmute);

  const wait = (durationMs: number): Promise<void> => {
    if (lifetime.signal.aborted) return Promise.resolve();
    return new Promise<void>((resolve) => {
      const done = (): void => {
        clearTimeout(timer);
        lifetime.signal.removeEventListener("abort", done);
        resolve();
      };
      const timer = setTimeout(done, durationMs);
      lifetime.signal.addEventListener("abort", done, { once: true });
    });
  };
  const yieldToUi = () => wait(0);

  let detectionPausedUntil = 0;
  let rejectedReadStreak = 0;
  const pauseDetection = (durationMs: number) => {
    if (!mobile) return;
    detectionPausedUntil = Math.max(detectionPausedUntil, performance.now() + durationMs);
  };
  // Do not start a detector run under a finger or behind a modal — on mobile, where the
  // UI and the detector share a main thread and a GPU process. The view reports the
  // holding; this is the policy about it.
  const uiBlocksDetection = (): boolean => mobile && inputs.interacting();

  /** The verdict half of a read: identify, suppress repeats, report. Off the frame loop. */
  const completeRead = async (sample: ReadSample) => {
    const { card, hq, quadFlat, windowAspect, collector, placementId, epoch } = sample;
    const readTook = clock();
    const identification = await identify(loaded, card, false, collector);
    if (!running) return;
    // Every verdict, rejections included, and its cost — `found` only carries the
    // successes, and a camera being tuned needs to see the failures it is causing.
    out.emit("read:done", {
      at: performance.now(),
      verdict: identification.verdict,
      reason: identification.reason,
      ms: readTook(),
      prepareMs: sample.prepareMs,
      latencyMs: performance.now() - sample.at,
    });

    if (identification.verdict === "ACCEPTED" || identification.verdict === "AMBIGUOUS") {
      rejectedReadStreak = 0;
    } else {
      rejectedReadStreak++;
      if (mobile && rejectedReadStreak >= REJECT_BACKOFF_AFTER) {
        pauseDetection(REJECT_BACKOFF_MS);
        log(
          "detect",
          `${rejectedReadStreak} consecutive rejected scans — backing off ${REJECT_BACKOFF_MS} ms`,
        );
      }
    }

    if (identification.verdict === "REJECTED" || !identification.printing) {
      if (!gate.ownsPlacement(placementId) || epoch !== evidenceEpoch) return;
      // The card is still lying there, and on a glossy sleeve the next frame carries
      // the sheen somewhere else — let the gate try again, a bounded number of times.
      if (gate.retake()) {
        log("verdict", "rejected — placement re-armed for another attempt");
        status("retrying…", "retrying");
      } else {
        // Said out loud, because from here the scanner does nothing at all until the card
        // moves, and a log that goes quiet looks the same as a scanner that is working.
        log(
          "verdict",
          `no attempts left for this placement — ${MAX_RETAKES + 1} reads over ` +
            `${RETAKE_DELAYS.reduce((a, b) => a + b, 0) / 1000}s all rejected ` +
            `(${identification.reason || "no match"}); it will not be read again until it moves`,
        );
        status(`${identification.reason || "no match"} — move the card`, "exhausted");
      }
      return;
    }

    // The funnel identified a card here, so the quad is vouched-for geometry — hand
    // it to the calibration feed before the repeat suppression can eat it.
    out.emit("read:identified", { quadFlat, windowAspect });

    const printing = identification.printing;
    const verdict = identification.verdict;
    // Key on the printing, not the artwork: two printings of one picture are
    // different answers, and the whole point of the decision layer is telling them
    // apart. An AMBIGUOUS reading keys on its leader, which is enough to stop the
    // same unresolved card filling the log.
    const key = captureKey(printing);
    const now = performance.now();
    const last = seen.get(key);
    seen.set(key, now);
    // Bounded: this only exists to suppress a repeat within REPEAT_MS, so anything
    // older than that window can never affect a decision again.
    if (seen.size > 512) {
      for (const [k, t] of seen) if (now - t > REPEAT_MS) seen.delete(k);
    }
    if (last != null && now - last < REPEAT_MS) {
      log(
        "verdict",
        `suppressed repeat of ${key} — seen ${((now - last) / 1000).toFixed(1)}s ago ` +
          `(window ${REPEAT_MS / 1000}s)`,
      );
      status(`${printing.name} — already logged`, "repeat");
      return;
    }

    log(
      "verdict",
      `logged ${identification.verdict}: ${printing.name} ` +
        `[${printing.setCode} ${printing.collector}] ${printing.rarity}, ${printing.artist}` +
        (identification.flipped ? " (read upside down)" : "") +
        // "Unverified" is a caveat when ORB could have spoken and did not; when the
        // retrieval evidence had already settled the verdict on its own it is a saving.
        (identification.verified
          ? ""
          : identification.settled
            ? " — settled on retrieval"
            : " — unverified"),
    );
    // The keepsake picture: encoded only for reads that actually get reported, and
    // persisted best-effort — losing a capture must never cost the scan. The HQ warp when
    // the GPU produced one, the recognition raster otherwise: a device on the 2D fallback
    // used to scan into a permanently empty history, which reads as a broken feature
    // rather than as a missing GPU.
    let encoded!: (blob: Blob | null) => void;
    const capture = new Promise<Blob | null>((resolve) => {
      encoded = resolve;
    });
    // The row goes out with the read's shortlist, which is what lets an AMBIGUOUS read be
    // resolved later — by the user, in a review, without this loop ever hearing of it.
    // The store's answer is the row's id; `found` does not wait for it.
    const at = Date.now();
    const captureId: Promise<number | null> = capture.then(async (blob) => {
      if (!blob) return null;
      try {
        return await saveCapture(
          {
            at,
            key,
            name: printing.name,
            setCode: printing.setCode,
            collector: printing.collector,
            verdict,
            illustration: printing.illustration,
            candidates: shortlist(identification.candidates),
          },
          blob,
        );
      } catch (error) {
        warn("capture", "could not save scan image", error);
        return null;
      }
    });
    const readId = crypto.randomUUID();
    const currentPlacement = gate.ownsPlacement(placementId) && epoch === evidenceEpoch;
    const confirmation =
      currentPlacement && latestPose?.placementId === placementId ? latestPose.frame : undefined;
    out.emit("found", {
      readId,
      identification,
      key,
      card,
      capture,
      captureId,
      quadFlat,
      windowAspect,
      confirmation,
    });
    const openPrinting = settings.ocr && !!loaded.ocr && printingStillOpen(identification);
    if (openPrinting && currentPlacement && gate.latched) {
      refinement = {
        readId,
        placementId,
        epoch,
        identification,
        attempts: 0,
        nextAt: performance.now() + COLLECTOR_RETRY_MS,
        closer: collector.height < COLLECTOR_MIN_HEIGHT,
      };
    }
    if (openPrinting && currentPlacement && collector.height < COLLECTOR_MIN_HEIGHT)
      status("come closer to read the collector number", "closer");
    else
      status(identification.verdict === "ACCEPTED" ? printing.name : `${printing.name}?`, "found");
    // Let the result listeners and chime finish before allocating capture canvases.
    // Keep this read's slot until encoding completes, bounding owned raster memory.
    // A Stop after the result must not cancel the image belonging to the logged row.
    await yieldToUi();
    try {
      encoded(await encodeCapture(hq ?? card, identification.flipped, hq ? HQ_SCALE : 1));
    } catch (error) {
      warn("capture", "could not encode scan image", error);
      encoded(null);
    }
  };

  /** Rolling per-stage cost, reported every `SUMMARY_FRAMES` so the log stays readable. */
  const SUMMARY_FRAMES = 20;
  let frames = 0;
  let grabTotal = 0;
  let detectTotal = 0;
  let probeTotal = 0;
  let announced = false;
  let summaryStartedAt: number | null = null;
  /** The framing last refused for its keystone, so the refusal is said once per framing. */
  let refusedFraming: Framing | null = null;
  /**
   * Consecutive failed iterations. One failure is a transient (a frame not yet
   * decodable, a dropped context); a streak means the engine itself is gone — a
   * corrupted onnxruntime heap fails every call forever, and warning once every 400 ms
   * for the rest of the session helps nobody. Stop and say so.
   */
  let failureStreak = 0;
  const FAILURE_STREAK_FATAL = 8;

  const scanLoop = async () => {
    while (running) {
      // Result cooldowns can sleep to their exact deadline. UI interaction is open-ended,
      // so poll it cheaply; camera presentation stays native at 30 fps throughout.
      const policyRemaining = detectionPausedUntil - performance.now();
      if (policyRemaining > 0 || uiBlocksDetection() || muted) {
        // Not looking is not the same as looking and seeing the card still there, and the
        // gate has to be told the difference or it will go on vouching for a placement it
        // has no current evidence for.
        gate.blind();
        resetFrameEvidence();
        await wait(policyRemaining > 0 ? policyRemaining : 50);
        continue;
      }
      // Wait for a frame produced after the previous iteration. Inference remains
      // single-flight and skipped camera frames are never queued or caught up later.
      // This wait also yields to UI work without a timer task after every detection.
      const fresh = await frameBoundary.next();
      if (!running) return;
      // A modal, track interruption or rejected read may have arrived while waiting.
      if (!fresh || muted || uiBlocksDetection() || detectionPausedUntil > performance.now()) {
        gate.blind();
        resetFrameEvidence();
        continue;
      }

      let sampledFrame: PixelSource | null = null;
      let gpuSample = false;
      try {
        // Read per iteration, never cached: the view re-frames whenever the viewport
        // changes — rotation, mobile browser chrome sliding in or out — and whenever
        // the user pans, zooms or drags the detection area, and a stale framing would
        // aim the scanner at a region they are no longer looking at.
        const framing = inputs.framing();
        if (!framing) {
          // The view has not said where to look yet — the first frames of a session,
          // before its framing lands. Nothing to scan through.
          resetFrameEvidence();
          await wait(60);
          continue;
        }
        const crop = framing.window;
        // Desk view is a projective sample and only the GL sampler can produce one; the
        // 2D letterbox would show the user a corrected image while scanning the raw one,
        // which is precisely the disagreement this architecture exists to prevent. Refuse
        // the framing and let the view drop the keystone.
        if (crop.keystone && !shared) {
          resetFrameEvidence();
          if (refusedFraming !== framing) {
            refusedFraming = framing;
            warn("detect", "desk view needs the WebGL sampler — asking the view to turn it off");
            out.emit("framing:rejected", { reason: "keystone" });
          }
          await wait(60);
          continue;
        }
        if (probeFraming !== framing) {
          resetFrameEvidence();
          gate.blind();
          probeFraming = framing;
        }
        // Cheap fresh-pixel evidence stays at camera cadence while inference is paced.
        // A changed same-position card asks the existing thumbnail proof to check the
        // latch; the probe itself never identifies or unlatches a card.
        const probeAt = performance.now();
        const changed = contentProbe.sample(surface, framing);
        probeTotal += performance.now() - probeAt;
        const now = performance.now();
        cadence.observeContent(changed, now);
        if (changed && gate.latched) gate.needsProof = true;
        if (!cadence.shouldStart(now)) continue;
        const processingStartedAt = now;
        const grabbed = clock();
        // What the detector is shown: the whole window, or the centred area the user
        // has cropped it to. Part of the framing for the same reason the window is —
        // the cropper moves under their finger while the loop runs.
        const area = framing.area;
        // Timestamp the sampled pixels rather than the later inference result, so
        // diagnostic consumers can measure how old the detection evidence is.
        const grabbedAt = performance.now();
        const detectedEpoch = captureEpoch;
        const sampledEpoch = evidenceEpoch;
        const pixels = (sampledFrame = retainFrame(surface));
        let frame: LetterboxedFrame | null =
          (await loaded.detector.letterboxVideo?.(pixels, crop, area)) ?? null;
        if (!running) return;
        if (
          muted ||
          detectedEpoch !== captureEpoch ||
          sampledEpoch !== evidenceEpoch ||
          inputs.framing() !== framing ||
          uiBlocksDetection()
        ) {
          gate.blind();
          resetFrameEvidence();
          continue;
        }
        if (!frame && shared) frame = shared.letterbox(pixels, crop, loaded.detector.width, area);
        // The canvas fallback may not stand in while desk view is on (same reason as
        // above); a transient GL miss then just waits for the next frame.
        if (!frame && !crop.keystone) {
          frame = letterboxCanvas(
            pixels,
            crop,
            loaded.detector.width,
            loaded.detector.height,
            area,
          );
        }
        if (!frame) {
          resetFrameEvidence();
          await wait(60);
          continue;
        }
        gpuSample = !!frame.gpu;
        const grabMs = grabbed();

        if (!announced) {
          announced = true;
          const sensorPixels = camera.sensorWidth * camera.sensorHeight;
          const visible = sensorPixels ? ((crop.sw * crop.sh) / sensorPixels) * 100 : 0;
          const cropped = framing.crop.w < 0.999 || framing.crop.h < 0.999;
          log(
            "detect",
            `sensor ${camera.sensorWidth}x${camera.sensorHeight} → on-screen scan region ` +
              `${crop.sw.toFixed(0)}x${crop.sh.toFixed(0)} (${visible.toFixed(1)}% of the frame)` +
              (cropped
                ? ` → detection area ${Math.round(framing.crop.w * 100)}% x ` +
                  `${Math.round(framing.crop.h * 100)}% of it, scanned ` +
                  `${area.w.toFixed(0)}x${area.h.toFixed(0)} with the ` +
                  `${CROP_MARGIN * 100}%-per-side margin`
                : "") +
              ` → letterboxed straight into ` +
              `${loaded.detector.width}x${loaded.detector.height} (x${frame.scale.toFixed(3)}, ` +
              `pad ${frame.left},${frame.top})`,
          );
        }

        const detected = clock();
        const found: PoseDetection[] = await loaded.detector.detect(ort, frame);
        const detectMs = detected();
        // Stopping or pausing during inference invalidates its result. Do not let an
        // old frame re-arm the gate, update the UI or launch recognition after a pause.
        if (!running) return;
        if (
          muted ||
          detectedEpoch !== captureEpoch ||
          sampledEpoch !== evidenceEpoch ||
          inputs.framing() !== framing ||
          uiBlocksDetection() ||
          detectionPausedUntil > performance.now()
        ) {
          gate.blind();
          resetFrameEvidence();
          continue;
        }
        const queueMs = loaded.detector.lastQueueMs ?? 0;
        const frameMs = grabMs + detectMs + probeTotal;
        probeTotal = 0;
        cadence.observeFrame(performance.now(), frameMs, queueMs);
        out.emit("detector:timing", {
          at: grabbedAt,
          grabMs,
          detectMs,
          queueMs,
          packMs: loaded.detector.lastPackMs,
          inferMs: loaded.detector.lastInferMs,
          frameMs: Math.max(0, frameMs - queueMs),
          ceilingFps: cadence.ceilingFps,
          targetFps: cadence.targetFps(performance.now()),
          gpu: !!frame.gpu,
        });
        // Quads arrive in the detection area's pixels; the area is a translation of the
        // window's own space, so putting them back is an offset — and every stage after
        // this one (the gate, the warp, the overlay, the calibration feed) goes on
        // working in window pixels, none of them aware there was a crop at all. Area
        // and aspect are translation-invariant, so they stand as computed.
        if (area.x || area.y) {
          for (const detection of found) {
            for (let i = 0; i < 8; i += 2) {
              detection.quad[i] += area.x;
              detection.quad[i + 1] += area.y;
            }
          }
        }
        const quads = found.map((detection) => detection.quad);

        frames++;
        summaryStartedAt ??= processingStartedAt;
        failureStreak = 0;
        grabTotal += grabMs;
        detectTotal += detectMs;
        trace(
          "frame",
          () =>
            `#${frames} letterbox ${ms(grabMs)} detect ${ms(detectMs)} (pack ${ms(loaded.detector.lastPackMs)} + infer ${ms(loaded.detector.lastInferMs)}) — ` +
            `${found.length} quad(s)` +
            (found.length
              ? ` best score ${found[0].score.toFixed(3)} area ${((found[0].area / (crop.sw * crop.sh)) * 100).toFixed(1)}% ` +
                `aspect ${found[0].aspect.toFixed(3)}`
              : "") +
            ` | gate held=${gate.held} tracked=${gate.tracked}` +
            (gate.lastVelocity == null ? "" : ` v=${gate.lastVelocity.toFixed(2)}/s`),
        );
        if (frames % SUMMARY_FRAMES === 0) {
          // N frame starts contain N-1 intervals. Reporting N / elapsed makes a strict
          // 10 fps limiter look faster in the first summary (20 / 1.9 s = 10.5).
          const processingFps =
            ((SUMMARY_FRAMES - 1) * 1000) / Math.max(1, processingStartedAt - summaryStartedAt);
          summaryStartedAt = null;
          log(
            "frame",
            `${frames} frames, ${(performance.now() / 1000).toFixed(1)}s — mean letterbox ` +
              `${ms(grabTotal / frames)}, detect ${ms(detectTotal / frames)} ` +
              `(${processingFps.toFixed(1)} effective fps, target ${cadence.targetFps(performance.now())}, ` +
              `ceiling ${cadence.ceilingFps}; frame work excluding queue ` +
              `${cadence.rollingFrameMs === null ? "warming" : ms(cadence.rollingFrameMs)})` +
              // Not available on Safari, but free to ask for and decisive where it is.
              (perfMemory() ? `, JS heap ${perfMemory()} MB` : ""),
          );
        }

        if (!found.length) {
          sharpened = null;
          refinement = null;
          latestPose = null;
          // Through the gate, not around it: a lost detection is a *gap* in a placement
          // until it has lasted long enough to be a removed card, and only the gate
          // knows the difference.
          gate.update(null);
          cadence.observeScene("empty", performance.now());
          out.emit("frame", { at: grabbedAt, window: crop, quads, still: false });
          status("looking…", "looking");
          continue;
        }

        const leading = found[0];
        const settled = gate.update(leading, readsInFlight === 0);
        if (sharpened && sharpened.placementId !== gate.placementId) sharpened = null;
        if (refinement && refinement.placementId !== gate.placementId) refinement = null;
        // The overlay's whole input: the quads, the window they are in, when they were
        // seen, and whether the leading one is resting. Sent after the gate has spoken
        // so the stillness call belongs to this frame, not the last.
        const currentFrame = { at: grabbedAt, window: crop, quads, still: gate.still };
        latestPose = { placementId: gate.placementId, frame: currentFrame };
        out.emit("frame", currentFrame);
        const sampleThumb = async (): Promise<Float32Array | null> => {
          try {
            return frame.gpu
              ? await loaded.detector.gpuThumbnail(frame, leading.quad, area)
              : quadThumb(frame, leading.quad, area, loaded.detector.width, loaded.detector.height);
          } catch (error) {
            warn("detect", "thumbnail unavailable — placement cannot be proven", error);
            return null;
          }
        };
        const frameStillValid = () =>
          running &&
          !muted &&
          detectedEpoch === captureEpoch &&
          sampledEpoch === evidenceEpoch &&
          inputs.framing() === framing &&
          !uiBlocksDetection();
        // A latch came back across a gap on geometry alone. Geometry has said everything
        // it can — the card is where the read one was — and the one thing it cannot see is
        // that this is a different card. So spend the thumbnail and ask.
        const proved = gate.needsProof;
        if (proved) {
          const thumb = await sampleThumb();
          if (!frameStillValid()) {
            gate.blind();
            resetFrameEvidence();
            continue;
          }
          // No reference means no proof, and no proof means read it: a card missed is
          // worse than a card read twice, and the log suppresses the second one anyway.
          const difference = latchThumb && thumb ? thumbDifference(latchThumb, thumb) : 1;
          const same = difference <= SWAP_DIFFERENCE;
          if (same) {
            gate.confirmed();
            // Kept fresh, so slow drift across a run of gaps never accumulates into one.
            latchThumb = thumb;
          } else {
            gate.swapped();
            refinement = null;
            sharpened = null;
          }
          log(
            "detect",
            `gap in the evidence: the card in frame is ` +
              `${same ? "the one that was read — latch kept" : "a different one — reading it"} ` +
              `(thumbnail Δ ${(difference * 100).toFixed(1)}%, swap over ${SWAP_DIFFERENCE * 100}%)`,
          );
        }
        // Worth saying out loud: without it, "the same card was read six times" and "the
        // detector blinked six times" look identical in the log.
        if (gate.takeResumed() && !proved) {
          log("detect", "detection came back in the same place — placement kept, not re-read");
        }
        cadence.observeScene(gate.latched ? "latched" : "active", performance.now());
        // A retry never repeats retrieval. It is tied to the current placement and
        // canceled across motion, blind time, Stop and re-framing, including late CTC.
        const pending = refinement;
        if (
          !settled &&
          pending &&
          gate.latched &&
          gate.still &&
          !gate.needsProof &&
          readsInFlight === 0
        ) {
          const height = sensorCardHeight(crop, leading.quad);
          pending.closer = height < COLLECTOR_MIN_HEIGHT;
          if (!settings.ocr || !loaded.ocr || pending.attempts >= COLLECTOR_MAX_RETRIES)
            refinement = null;
          else if (!pending.closer && performance.now() >= pending.nextAt) {
            pending.attempts++;
            pending.nextAt = performance.now() + COLLECTOR_RETRY_MS;
            if (frame.gpu) shared?.refreshFrame(pixels);
            const strips = collectorSampler.read(
              pixels,
              crop,
              leading.quad,
              pending.identification.flipped,
              shared,
            );
            if (strips) {
              readsInFlight++;
              const retryAt = performance.now();
              void refineCollector(loaded, pending.identification, strips)
                .then((result) => {
                  if (
                    !running ||
                    muted ||
                    uiBlocksDetection() ||
                    refinement !== pending ||
                    pending.epoch !== evidenceEpoch ||
                    !gate.ownsPlacement(pending.placementId) ||
                    !gate.latched ||
                    gate.needsProof
                  )
                    return;
                  log(
                    "read",
                    `collector retry ${pending.attempts}/${COLLECTOR_MAX_RETRIES}: ${ms(performance.now() - retryAt)}${result ? " — confirmed" : " — unresolved"}`,
                  );
                  if (result?.printing) {
                    seen.set(captureKey(result.printing), performance.now());
                    out.emit("found:updated", { readId: pending.readId, identification: result });
                    status(result.printing.name, "found");
                    refinement = null;
                  }
                })
                .catch((error) => {
                  if (running) warn("read", "collector retry failed", error);
                })
                .finally(() => {
                  pending.nextAt = performance.now() + COLLECTOR_RETRY_MS;
                  readsInFlight--;
                });
            }
          }
        }
        if (!settled) {
          // The gate fires once per placement, so every later frame of a card still lying
          // there also answers false. Counting up to 3 again would be a lie — the card is
          // read, and what the user is waiting on now is the next one.
          if (readsInFlight > 0) status("reading…", "reading");
          else if (gate.exhausted) status("no match — move the card", "exhausted");
          else if (refinement?.closer) status("come closer to read the collector number", "closer");
          else if (gate.latched) status("ready for the next card", "done");
          else status(`holding… ${Math.min(gate.held, HOLD_LABEL)}/${HOLD_LABEL}`, "holding");
          continue;
        }
        // What the card that is about to latch the placement looks like, measured before
        // the read rather than after it: this is the reference every later gap in the
        // evidence is proven against, and it has to come from the same frame the quad did.
        latchThumb = await sampleThumb();
        if (!frameStillValid()) {
          gate.blind();
          resetFrameEvidence();
          continue;
        }
        log(
          "detect",
          `settled after ${gate.tracked} tracked frame(s) (${gate.fireReason}) — ` +
            `score ${leading.score.toFixed(3)}, aspect ${leading.aspect.toFixed(3)} ` +
            `(card is 0.716), quad [${Array.from(leading.quad, (v) => Math.round(v)).join(", ")}]`,
        );

        // Warp from the sharpest pixels available, not from the detection raster: the
        // quad is only as good as the corners, but the card is only as good as the
        // resolution it is read at.
        const warped = clock();
        // The GPU path samples the video texture directly, so it needs neither the
        // intermediate raster nor its readback. Falling back costs both.
        let card = shared ? shared.warpCard(pixels, crop, leading.quad, 1, !frame.gpu) : null;
        let source = "GPU";
        if (!card) {
          // Preserve the existing canonical crop/warp path. Only a quad extending
          // beyond that crop needs sensor pixels recovered by overscan.
          const flat = crop.keystone ? keystonePx(crop) : null;
          const flatQuad = new Float32Array(8);
          for (let i = 0; i < 8; i += 2) {
            const [x, y] = flat
              ? applyHomography(flat, leading.quad[i], leading.quad[i + 1])
              : [leading.quad[i], leading.quad[i + 1]];
            flatQuad[i] = x;
            flatQuad[i + 1] = y;
          }
          const outside = flatQuad.some((v, i) => v < 0 || v > (i % 2 ? crop.sh : crop.sw));
          const sensorWindow = {
            sw: camera.sensorWidth,
            sh: camera.sensorHeight,
            affine: [1, 0, 0, 0, 1, 0] as const,
            keystone: 0,
            keystoneSpan: camera.sensorHeight,
          };
          const sourceWindow = outside ? sensorWindow : crop;
          const full = grabWarp(
            pixels,
            sourceWindow,
            outside
              ? Math.max(camera.sensorWidth, camera.sensorHeight)
              : warpRasterFor(leading, crop),
          );
          if (!full) {
            // The gate has latched this placement on a read that is now not going to
            // happen. Hand the attempt back, or the card sits there having been decided
            // about without anything ever having looked at it.
            gate.retake();
            continue;
          }
          // The raster is the flat window, but the quad lives in view space: under desk
          // view the corners go through the keystone first. Exact either way — the warp
          // is solved from the four corners, wherever they land.
          const quad = outside ? sensorQuad(crop, leading.quad) : flatQuad;
          const up = full.width / sourceWindow.sw;
          for (let i = 0; i < quad.length; i++) quad[i] *= up;
          card = warpCard(full.data, full.width, full.height, quad);
          source = `CPU ${full.width}x${full.height}`;
        }
        if (!card || card.length !== CARD_W * CARD_H * 4) {
          warn("detect", "warp failed — quad degenerate");
          gate.retake();
          continue;
        }
        // The upscale factor is the number that predicts a soft read: above ~2x the
        // hashes and ORB are both looking at interpolation rather than at the card.
        const cardHeight = sensorCardHeight(crop, leading.quad);
        // Measured before the read is spent, and reported next to the upscale factor —
        // the two together are what predicts a soft read, and a rejection with both
        // numbers beside it says which of the card and the picture of it was the problem.
        const focus = focusScore(card);
        focusBar = Math.max(focus, focusBar * FOCUS_DECAY);
        log(
          "detect",
          `warped ${ms(warped())} via ${source} → ${CARD_W}x${CARD_H}; ` +
            `card was ${cardHeight.toFixed(0)}px tall in the scan region, ` +
            `scale x${(CARD_H / cardHeight).toFixed(2)}, focus ${focus.toFixed(3)} ` +
            `(bar ${(FOCUS_RATIO * focusBar).toFixed(3)})`,
        );
        // A card that has just landed is still being focused on, and identifying that crop
        // answers a question about the lens rather than about the card. Hold the attempt —
        // it costs nothing, it is bounded, and the frame a second later is the one worth
        // spending it on.
        const deferred =
          !sharpened && focus < FOCUS_RATIO * focusBar && gate.defer(FOCUS_RETRY_MS, FOCUS_WAIT_MS);
        // The sharpness of every crop that reached this point, held or spent: the number a
        // camera-control experiment is trying to move, reported whether or not it did.
        out.emit("read:sampled", {
          at: performance.now(),
          focus,
          bar: FOCUS_RATIO * focusBar,
          deferred,
          cardHeight,
          upscale: CARD_H / cardHeight,
        });
        if (deferred) {
          log(
            "detect",
            `crop too soft to read — focus ${focus.toFixed(3)} under the bar ` +
              `${(FOCUS_RATIO * focusBar).toFixed(3)}; waiting ${FOCUS_RETRY_MS}ms for a ` +
              `sharper frame (attempt not spent)`,
          );
          status("focusing…", "focusing");
          continue;
        }

        status("reading…", "reading");
        // The keepsake capture: the same homography sampled at twice the density, off
        // the frame the quad was detected on (the texture is still resident). Taken at
        // dispatch because the video moves on while the read runs detached.
        const hqView =
          shared && source === "GPU" && captureScale > 1
            ? shared.warpCard(pixels, crop, leading.quad, captureScale)
            : null;
        // Owned copies, because the read now outlives this frame: the GPU path hands
        // out views over shared readback buffers that the next warp overwrites.
        const snapshot = new Uint8ClampedArray(card);
        const hqSnapshot = hqView ? new Uint8ClampedArray(hqView) : null;
        const legibleSize = settings.ocr && !!loaded.ocr && cardHeight >= COLLECTOR_MIN_HEIGHT;
        const collector: CollectorEvidence = legibleSize
          ? collectorSampler.capture(pixels, crop, leading.quad, settings.flipCheck, shared)
          : { height: cardHeight, upright: null, flipped: null };
        // The quad in uniform flat-window units, for the desk-view auto calibration —
        // through the current keystone and scaled by one divisor on both axes, so the
        // calibrator measures raw trapezoid geometry with shapes intact, regardless of
        // the correction in force.
        const flatForCalibration = crop.keystone ? keystonePx(crop) : null;
        const quadFlat = new Float32Array(8);
        for (let i = 0; i < 8; i += 2) {
          const [x, y] = flatForCalibration
            ? applyHomography(flatForCalibration, leading.quad[i], leading.quad[i + 1])
            : [leading.quad[i], leading.quad[i + 1]];
          quadFlat[i] = x / crop.keystoneSpan;
          quadFlat[i + 1] = y / crop.keystoneSpan;
        }
        let sample: ReadSample = {
          card: snapshot,
          hq: hqSnapshot,
          collector,
          quadFlat,
          windowAspect: crop.sw / crop.keystoneSpan,
          placementId: gate.placementId,
          epoch: evidenceEpoch,
          at: grabbedAt,
          prepareMs: warped(),
          focus,
          score: leading.score,
          aspect: leading.aspect,
          thumb: latchThumb,
        };
        if (sharpened) {
          sample = sharperSample(sharpened, sample, grabbedAt - sharpened.at);
          sharpened = null;
        } else if (legibleSize && gate.still) {
          sharpened = sample;
          gate.sharpen();
          cadence.observeScene("active", performance.now());
          status("focusing…", "focusing");
          continue;
        }
        latchThumb = sample.thumb;
        readsInFlight++;
        void completeRead(sample)
          .catch((error) => {
            if (!running) return;
            warn("frame", "read failed", error);
            status("error — see console", "error");
          })
          .finally(() => {
            readsInFlight--;
          });
      } catch (error) {
        if (!running) return;
        resetFrameEvidence();
        warn("frame", "scan iteration failed", error);
        failureStreak++;
        if (failureStreak >= FAILURE_STREAK_FATAL) {
          warn(
            "detect",
            `${failureStreak} consecutive scan iterations failed — the inference engine ` +
              `is not coming back; stopping the loop`,
          );
          status("scanner crashed — reload the page", "crashed");
          running = false;
          frameBoundary.stop();
          contentProbe.dispose();
          lifetime.abort();
          return;
        }
        status("error — see console", "error");
        await wait(400);
      } finally {
        releaseFrame(sampledFrame);
        if (gpuSample) shared?.releaseVideo();
      }
    }
  };

  let started = false;
  let stopped = false;
  return {
    run() {
      if (started || !running) return;
      started = true;
      // A track that opened paused says so now; the loop would otherwise sit silent.
      if (muted) onMute();
      void scanLoop();
    },
    stop() {
      // `running` alone is not the flag: a crashed loop has already cleared it, and the
      // camera it left open still has to be released.
      if (stopped) return;
      stopped = true;
      running = false;
      frameBoundary.stop();
      contentProbe.dispose();
      collectorSampler.dispose();
      shared?.dispose();
      loaded.detector.releaseVideo?.();
      sharpened = null;
      refinement = null;
      lifetime.abort();
      surface.removeEventListener("resize", onSurfaceResize);
      track?.removeEventListener("mute", onMute);
      track?.removeEventListener("unmute", onUnmute);
      closeCamera(surface, camera);
      log(
        "detect",
        `camera stopped after ${frames} frames — mean detect ${ms(detectTotal / (frames || 1))}, ` +
          `${seen.size} distinct printing(s) seen`,
      );
    },
  };
}
